import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { downloadDocument } from '../lib/documentPreview';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { DocumentPreviewModal } from '../components/DocumentPreviewModal';
import { 
  FileText, 
  Download, 
  Eye, 
  Loader2,
  Wallet,
  Calendar,
  Filter,
  ChevronDown,
  X
} from 'lucide-react';

const MONTHS = [
  { value: '01', label: 'Ocak' },
  { value: '02', label: 'Şubat' },
  { value: '03', label: 'Mart' },
  { value: '04', label: 'Nisan' },
  { value: '05', label: 'Mayıs' },
  { value: '06', label: 'Haziran' },
  { value: '07', label: 'Temmuz' },
  { value: '08', label: 'Ağustos' },
  { value: '09', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' }
];

export default function MyPayslips() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [loonbriefs, setLoonbriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Filter states
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchLoonbriefs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchLoonbriefs = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/loonbrief/user/${user.id}`);
      if (response.data.success) {
        setLoonbriefs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching loonbriefs:', error);
      toast.error('Bodrolar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (docId, fileName) => {
    const ok = await downloadDocument(api, { id: docId, original_name: fileName || 'loonbrief.pdf' });
    if (!ok) toast.error('Dosya indirilirken hata oluştu');
  };

  const handlePreview = (doc) => {
    setPreviewDoc({
      id: doc.id,
      file_name: doc.file_name,
      file_type: 'application/pdf'
    });
  };

  // Get available years from loonbriefs
  const availableYears = useMemo(() => {
    const years = new Set();
    loonbriefs.forEach(lb => {
      const year = lb.period ? lb.period.split('-')[0] : new Date(lb.created_at).getFullYear().toString();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [loonbriefs]);

  // Toggle month selection
  const toggleMonth = (monthValue) => {
    setSelectedMonths(prev => 
      prev.includes(monthValue) 
        ? prev.filter(m => m !== monthValue)
        : [...prev, monthValue]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedYear('all');
    setSelectedMonths([]);
  };

  // Filter loonbriefs based on selection
  const filteredLoonbriefs = useMemo(() => {
    return loonbriefs.filter(lb => {
      const period = lb.period || '';
      const year = period ? period.split('-')[0] : new Date(lb.created_at).getFullYear().toString();
      const month = period ? period.split('-')[1] : (new Date(lb.created_at).getMonth() + 1).toString().padStart(2, '0');
      
      // Year filter
      if (selectedYear !== 'all' && year !== selectedYear) {
        return false;
      }
      
      // Month filter (if any months selected)
      if (selectedMonths.length > 0 && !selectedMonths.includes(month)) {
        return false;
      }
      
      return true;
    });
  }, [loonbriefs, selectedYear, selectedMonths]);

  // Group filtered loonbriefs by year
  const groupedByYear = useMemo(() => {
    return filteredLoonbriefs.reduce((acc, lb) => {
      const year = lb.period ? lb.period.split('-')[0] : new Date(lb.created_at).getFullYear().toString();
      if (!acc[year]) acc[year] = [];
      acc[year].push(lb);
      return acc;
    }, {});
  }, [filteredLoonbriefs]);

  // Sort years descending
  const sortedYears = Object.keys(groupedByYear).sort((a, b) => b - a);

  // Check if any filters are active
  const hasActiveFilters = selectedYear !== 'all' || selectedMonths.length > 0;

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.myPayslips') || 'Bodrolarım'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('employeeProfile.payslipsDesc') || 'Aylık bordro dökümanlarınız'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchLoonbriefs} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.refresh') || 'Yenile'}
        </Button>
      </div>

      {/* Period Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('common.period') || 'Dönem'}:
              </span>
            </div>

            {/* Year Filter */}
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32 h-9">
                <SelectValue placeholder="Yıl Seç" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Yıllar</SelectItem>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Month Multi-Select */}
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-9 min-w-[140px] justify-between"
                onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
              >
                <span className="truncate">
                  {selectedMonths.length === 0 
                    ? 'Ay Seç' 
                    : selectedMonths.length === 1 
                      ? MONTHS.find(m => m.value === selectedMonths[0])?.label
                      : `${selectedMonths.length} ay seçili`}
                </span>
                <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
              </Button>
              
              {monthDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setMonthDropdownOpen(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-2 min-w-[200px]">
                    <div className="grid grid-cols-2 gap-1">
                      {MONTHS.map(month => (
                        <label
                          key={month.value}
                          className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer text-sm"
                        >
                          <Checkbox
                            checked={selectedMonths.includes(month.value)}
                            onCheckedChange={() => toggleMonth(month.value)}
                          />
                          <span className="text-slate-700 dark:text-slate-300">{month.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 mt-2 pt-2 flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1 h-7 text-xs"
                        onClick={() => setSelectedMonths(MONTHS.map(m => m.value))}
                      >
                        Tümünü Seç
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1 h-7 text-xs"
                        onClick={() => setSelectedMonths([])}
                      >
                        Temizle
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <X className="h-4 w-4 mr-1" />
                Filtreleri Temizle
              </Button>
            )}

            {/* Result Count */}
            <div className="ml-auto">
              <Badge variant="secondary" className="text-xs">
                {filteredLoonbriefs.length} / {loonbriefs.length} bordro
              </Badge>
            </div>
          </div>

          {/* Selected Months Tags */}
          {selectedMonths.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <span className="text-xs text-slate-500 dark:text-slate-400 self-center">Seçili aylar:</span>
              {selectedMonths.sort().map(monthValue => {
                const month = MONTHS.find(m => m.value === monthValue);
                return (
                  <Badge 
                    key={monthValue} 
                    variant="outline" 
                    className="gap-1 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
                    onClick={() => toggleMonth(monthValue)}
                  >
                    {month?.label}
                    <X className="h-3 w-3" />
                  </Badge>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : loonbriefs.length === 0 ? (
        <Card className="p-12 text-center">
          <Wallet className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
            {t('employeeProfile.noPayslips') || 'Henüz bordro yüklenmemiş'}
          </h3>
          <p className="text-sm text-slate-400">
            {t('employeeProfile.noPayslipsDesc') || 'Bordro dökümanlarınız yüklendiğinde burada görüntülenecektir'}
          </p>
        </Card>
      ) : filteredLoonbriefs.length === 0 ? (
        <Card className="p-12 text-center">
          <Filter className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">
            Filtreye uygun bordro bulunamadı
          </h3>
          <p className="text-sm text-slate-400 mb-4">
            Seçili dönem için bordro kaydı yok
          </p>
          <Button variant="outline" onClick={clearFilters}>
            Filtreleri Temizle
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedYears.map(year => (
            <Card key={year}>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-emerald-600" />
                  {year}
                  <Badge variant="secondary" className="ml-2">
                    {groupedByYear[year].length} {t('common.document') || 'döküman'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {groupedByYear[year]
                    .sort((a, b) => (b.period || '').localeCompare(a.period || ''))
                    .map((lb) => (
                    <div 
                      key={lb.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">
                            {lb.period ? (
                              new Date(lb.period + '-01').toLocaleString('tr-TR', { year: 'numeric', month: 'long' })
                            ) : lb.file_name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {new Date(lb.created_at).toLocaleDateString('tr-TR')} tarihinde yüklendi
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(lb)}
                          className="gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          <span className="hidden sm:inline">{t('documents.preview') || 'Görüntüle'}</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(lb.id, lb.file_name)}
                          className="gap-1"
                        >
                          <Download className="h-4 w-4" />
                          <span className="hidden sm:inline">{t('documents.download') || 'İndir'}</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Document Preview Modal */}
      <DocumentPreviewModal 
        document={previewDoc}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </div>
  );
}
